import { classeParaLabelsFormularioComFundoEscuro } from "app/estilos/classeParaLabelsFormularioComFundoEscuro";
import { IOrcamento } from "app/types/IOrcamento";
import { DadosResumoOrcamento, ObterResumoOrcamento } from "app/types/Resumo";
import clsx from "clsx";
import { FormikErrors, useFormik } from "formik";
import globalMU from "globalMU";
import * as R from 'ramda';
import React, { memo, useEffect, useRef } from "react";
import { SelecaoComFiltro } from "../selecao-com-filtro/SelecaoComFiltro";
import { InputData } from "../inputs/InputData";
import store from "app/redux/store/store";
import { gravarTelasAlteradasNaoSalvaAction } from "app/redux/telaOrcamentoAlteradaNaoSalvaSlice";
import { atualizarCamposPendentesOrcamentoEmAndamentoAction } from "app/redux/orcamentoEmAndamentoSlice";
import { ParametroPorItem } from "app/types/Parametro";

const estilosFormulario = require('../../estilos/formulario.module.scss').default
const estilosBotoes = require('../../estilos/botoes.module.scss').default
const estilosInputs = require('../../estilos/inputs.module.scss').default

export type ResumoOrcamentoProps = {
  dados: DadosResumoOrcamento;
  tipoOrcamentoDescricao: IOrcamento["TipoOrcamentoDescricao"]
  orcamentoConfirmado: boolean;
  apenasConfirmado: boolean;
  enviarParaSupervisao: boolean;
  apenasOrcamento: boolean;
  permitirAlterarTPO: boolean;
  permitirMostrarAreaVenda: boolean;
  permitirPrevisaoEntregaPorItem: boolean;
  permitirVendaComEntregaPosterior: boolean;
  somenteLeitura: boolean;
  processando: boolean
  parametrosPorFilial?: ParametroPorItem[] // 487678
  onAplicar: (dados: ObterResumoOrcamento) => void;
  onErrorValidacao: (erros: FormikErrors<DadosResumoOrcamento>) => void;
};

export const ResumoOrcamento = memo((props: ResumoOrcamentoProps) => {

  const valorParametroPorFilial = (nome: string) => props.parametrosPorFilial.find(p => p.Nome === nome)?.Valor;
  const formik = useFormik({
    initialValues: {
      ...props.dados?.ObterResumoOrcamento,
      PedidoCompraCliente: props.dados?.PedidoCompraCliente ?? "",
      TipoOperacao: props.dados?.ObterResumoOrcamento?.TipoOperacao == 0 ? props.dados?.TiposOperacao[0].Oid : props.dados?.TiposOperacao?.find((x) => props.dados?.ObterResumoOrcamento?.TipoOperacao == x.Oid)?.Oid,
      TipoOperacaoDescricao: props.dados?.ObterResumoOrcamento?.TipoOperacao == 0 ? props.dados?.TiposOperacao[0].Descricao : props.dados?.TiposOperacao?.find((x) => props.dados?.ObterResumoOrcamento?.TipoOperacao == x.Oid)?.Descricao,
      DataValidade: globalMU.converterDataHoraFormatoENComTParaDataFormatoEN(props.dados?.ObterResumoOrcamento?.DataValidade)
    },
    onSubmit: (values, { }) => { },
    enableReinitialize: true,
    validate: (values) => {

      const mensagemObrigatorio = (campo) => `O campo "${campo}" é obrigatório.`

      return R.pickBy(value => globalMU.isNotNullOrEmpty(value), {
        TipoOperacao: !values.TipoOperacao ? mensagemObrigatorio("Tipo de Operação") : undefined,
        FinalidadeVenda: formik.values.RequisitarFinalidadeVenda && globalMU.isNullOrEmpty(values.FinalidadeVenda) ? mensagemObrigatorio("Finalidade") : undefined,
        DataValidade: globalMU.isNullOrEmpty(values.DataValidade) || !globalMU.dataNoFormatoENEhValida(values.DataValidade) ? mensagemObrigatorio("Data Validade") : undefined,
        Situacao: globalMU.isNullOrEmptyOrZero(values.Situacao) ? mensagemObrigatorio("Opções de Orçamento") : undefined,
      })
    },
  });


  const jaFocouAoEntrar = useRef(false)

  // Ao entrar na tela: foco no Tipo de Operação se ele estiver habilitado; senão, no próximo
  // campo habilitado do formulário. Como ele é o primeiro <input>/<select>/<textarea> real no DOM
  // (os campos antes dele são só texto/labels de leitura), ":not(:disabled)" já resolve os dois
  // casos sozinho: se ele estiver habilitado, casa nele; senão, pula pro próximo habilitado.
  useEffect(() => {
    if (props.processando || jaFocouAoEntrar.current) return
    jaFocouAoEntrar.current = true

    const primeiroCampoHabilitado = document.querySelector<HTMLElement>(
      "#ResumoOrcamento_Formulario input:not(:disabled), " +
      "#ResumoOrcamento_Formulario select:not(:disabled), " +
      "#ResumoOrcamento_Formulario textarea:not(:disabled)"
    )
    primeiroCampoHabilitado?.focus()
  }, [props.processando])

  // FilialDestinoCodigo só existe em ObterResumoOrcamento (sem representação em IOrcamento) --
  // captura no snapshot de orçamento em andamento assim que chega do backend, senão a recuperação
  // de uma Transferência perde a filial de destino (ver _RestaurarDadosPendentesClassesFilhasAntesItens
  // em OrcamentoTransferencia.prg).
  useEffect(() => {
    if (props.tipoOrcamentoDescricao == "Transferências" && !globalMU.isNullOrEmpty(props.dados?.ObterResumoOrcamento?.FilialDestinoCodigo)) {
      store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ FilialDestinoCodigo: props.dados.ObterResumoOrcamento.FilialDestinoCodigo }))
    }
  }, [props.dados?.ObterResumoOrcamento?.FilialDestinoCodigo])

  const dadosTransferenciaFilialDestino = <>
    {props.tipoOrcamentoDescricao == "Transferências" && (
      <>
        <label id="ResumoOrcamento_LabelFilialDestino">Filial Destino</label>
        <span id="ResumoOrcamento_FilialDestino" className="_bg-[#ffffff] _font-bold _ml-[-10px]
         _pl-[10px] _mb-[-5px] _pb-[5px]">
          {formik.values.FilialDestino?.RazaoSocial}
        </span>

        <label id="ResumoOrcamento_LabelCnpjFilialDestino">CNPJ</label>
        <span id="ResumoOrcamento_CnpjFilialDestino">{formik.values.FilialDestino?.CpfCnpj}</span>
      </>
    )}
  </>

  const dadosVendaCliente = <>
    {props.tipoOrcamentoDescricao != "Transferências" && (
      <>
        <label id="ResumoOrcamento_LabelNomeCliente">Nome do Cliente</label>
        <span id="ResumoOrcamento_NomeCliente" className="_bg-[#ffffff] _font-bold _ml-[-10px]
         _pl-[10px] _mb-[-5px] _pb-[5px]">
          {formik.values.NomeCliente}
        </span>

        <label id="ResumoOrcamento_LabelCpfCnpjCliente">CNPJ / CPF</label>
        <span id="ResumoOrcamento_CpfCnpjCliente">{formik.values.CpfCnpjCliente}</span>
      </>
    )}
  </>

  const dadosFilialVenda = <>
    <label id="ResumoOrcamento_LabelFilialVenda">Filial da Venda</label>
    <div id="ResumoOrcamento_FilialVenda">{formik.values.FilialVendaDescricao}</div>
  </>

  const dadosAreaVenda = <>
    {props.tipoOrcamentoDescricao != "Transferências" &&
      props.permitirMostrarAreaVenda && (
        <>
          <label id="ResumoOrcamento_LabelAreaVenda">Área de Venda</label>
          <div id="ResumoOrcamento_AreaVenda">{formik.values.NomeAreaVenda}</div>
        </>
      )}
  </>

  const dadosPlanoPagamentoSemDescricao = <>
    {props.tipoOrcamentoDescricao != "Transferências" &&
      !(formik.values.DescricaoPlanoPagamento == null || formik.values.DescricaoPlanoPagamento == "") && (
        <>
          <label id="ResumoOrcamento_LabelPlanoPagamento">Plano de Pagamento</label>
          <div id="ResumoOrcamento_PlanoPagamento">{formik.values.DescricaoPlanoPagamento}</div>

          <label id="ResumoOrcamento_LabelParcelas">Parcelas</label>
          <div id="ResumoOrcamento_Parcelas">{formik.values.QuantidadeParcelasPlanoPagamento}</div>

          <label id="ResumoOrcamento_LabelCarencia">Carência</label>
          <div id="ResumoOrcamento_Carencia">{formik.values.DiasCarenciaPlanoPagamento}</div>

          <label id="ResumoOrcamento_LabelDocumentoPlano">Documento Plano</label>
          <div id="ResumoOrcamento_DocumentoPlano">{formik.values.NomeDocumentoPlanoPagamento}</div>

          <label id="ResumoOrcamento_LabelComEntrada">Com Entrada</label>
          <div id="ResumoOrcamento_ComEntrada">{formik.values.PlanoPagamentoComEntrada ? "Sim" : "Não"}</div>

          {formik.values.PlanoPagamentoComEntrada && (
            <>
              <label id="ResumoOrcamento_LabelDocumentoEntrada">Documento Entrada</label>
              <div id="ResumoOrcamento_DocumentoEntrada">{formik.values.NomeDocumentoEntrada}</div>

              <label id="ResumoOrcamento_LabelValorEntrada">Valor Entrada</label>
              <div id="ResumoOrcamento_ValorEntrada">
                {globalMU.formatarValorNumerico(
                  formik.values.ValorEntrada,
                  2,
                  true
                )}
              </div>
            </>
          )}
        </>
      )}
  </>

  const dadosPlanoPagamentoComDescricao = <>
    {props.tipoOrcamentoDescricao != "Transferências" &&
      (formik.values.DescricaoPlanoPagamento == null || formik.values.DescricaoPlanoPagamento == "") && (
        <>
          <label id="ResumoOrcamento_LabelFormaPagamento">Forma de Pagamento</label>
          <div id="ResumoOrcamento_FormaPagamento">{formik.values.NomeFormaPagamento}</div>

          <label id="ResumoOrcamento_LabelTipoDocumento">Tipo de Documento</label>
          <div id="ResumoOrcamento_TipoDocumento">{formik.values.DescricaoTipoDocumento}</div>
        </>
      )}
  </>

  const dadoDespesasInclusas = <>
    {props.tipoOrcamentoDescricao != "Transferências" && (
      <>
        <label id="ResumoOrcamento_LabelDespesasInclusas">Total de Despesas Inclusas</label>
        <div id="ResumoOrcamento_DespesasInclusas">
          {globalMU.formatarValorNumerico(formik.values.ValorDespesasInclusas, 2, true)}
        </div>
      </>
    )}
  </>

  const dadosDateOrcamento = <>
    <label id="ResumoOrcamento_LabelDataPrevista">Data Prevista</label>
    <div id="ResumoOrcamento_DataPrevista">{globalMU.formatDate(formik.values.DataPrevisaoEntrega)}</div>

    {props.permitirPrevisaoEntregaPorItem && (
      <>
        <label id="ResumoOrcamento_LabelDataMarcada">Data Marcada</label>
        <div id="ResumoOrcamento_DataMarcada">{globalMU.formatDate(formik.values.DataMarcadaEntrega)}</div>
      </>
    )}
  </>


  const dadosEntregaPosteriorTurnoEntrega = <>
    {props.tipoOrcamentoDescricao != "Transferências" && (
      <>
        {props.permitirPrevisaoEntregaPorItem && (
          <>
            <label id="ResumoOrcamento_LabelTurnoEntrega">Turno</label>
            <div id="ResumoOrcamento_TurnoEntrega">{formik.values.DescricaoTurnoEntrega}</div>
          </>
        )}

        {props.permitirVendaComEntregaPosterior && (
          <>
            <label id="ResumoOrcamento_LabelEntregaPosterior">Entrega Posterior</label>
            <div id="ResumoOrcamento_EntregaPosterior">{formik.values.EntregaPosterior ? "Sim" : "Não"}</div>
          </>
        )}
      </>
    )}
  </>


  const dadosMonetariosOrcamento = <>
    <label id="ResumoOrcamento_LabelTotalMercadorias">Total de Mercadorias</label>
    <div id="ResumoOrcamento_TotalMercadorias">
      {globalMU.formatarValorNumerico(
        formik.values.ValorTotalMercadorias,
        2,
        true
      )}
    </div>

    <label id="ResumoOrcamento_LabelValorTotal">Valor Total</label>
    <div id="ResumoOrcamento_ValorTotal">
      {globalMU.formatarValorNumerico(formik.values.ValorTotal, 2, true)}
    </div>
  </>

  const campoSelecionarTipoDeOperacao = <>
    <label
      id="ResumoOrcamento_LabelTipoOperacao"
      title="Veja segurança [Permitir Alterar TPO] e parâmetro [Permite alterar TPO no Orçamento / Devolução]"
      >Tipo de operação <p className={estilosInputs.obrigatorio}>*</p></label>
    <SelecaoComFiltro
      id="ResumoOrcamento_TipoOperacao"
      itens={(props.dados?.TiposOperacao ?? []).map(tipoOperacao => ({
        chave: tipoOperacao.Oid.toString(),
        valor: tipoOperacao.Descricao
      }))}
      onItemSelecionado={item => {
        const tipoOperacaoSelecionado = (props?.dados?.TiposOperacao ?? []).find(t => t.Oid.toString() == item.chave).Oid
        formik.setFieldValue("TipoOperacao", tipoOperacaoSelecionado)
        formik.setFieldValue("TipoOperacaoDescricao", item.valor)
        store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
        store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ TipoOperacao: tipoOperacaoSelecionado }))
      }}
      itemSelecionado={{
        chave: formik.values.TipoOperacao,
        valor: formik.values.TipoOperacaoDescricao
      }}
      disabled={!props.permitirAlterarTPO || props.somenteLeitura || props.processando}
      placeholder="Digite 1 ou mais caracteres"
    />
  </>

  const campoInformarPedidoDeCompra = <>
    {props.tipoOrcamentoDescricao != "Transferências" && (
      <>
        <label id="ResumoOrcamento_LabelPedidoCompra">Pedido de Compra</label>
        <div id="ResumoOrcamento_PedidoCompraContainer">
          <input
            name="PedidoCompraCliente"
            id="ResumoOrcamento_PedidoCompra"
            maxLength={15}
            type="text"
            value={(formik.values.PedidoCompraCliente) ?? ""}
            onChange={(e) => {
              formik.setFieldValue("PedidoCompraCliente", e.target.value);
              store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
              store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ PedidoCompraCliente: e.target.value }))
            }}
            disabled={props.somenteLeitura || props.processando}
          />
        </div>
      </>
    )}
  </>

  const campoSelecionarOpcaoOrcamento = <>
    <label id="ResumoOrcamento_LabelOpcoesOrcamento">
      <u>O</u>pções de Orçamento <p className={estilosInputs.obrigatorio}>*</p>
    </label>
    <div id="ResumoOrcamento_OpcoesOrcamentoContainer">
      <div
        id="ResumoOrcamento_OpcoesOrcamentoRadioGroup"
        className={"_flex _flex-row _gap-2"}
      >
        {props.apenasOrcamento && (
          <label id="ResumoOrcamento_LabelApenasOrcamento" style={{ cursor: "pointer" }}>
            <input accessKey="o"
              type="radio"
              name="Situacao"
              id="ResumoOrcamento_Situacao_ApenasOrcamento"
              disabled={props.somenteLeitura || props.processando}
              checked={formik.values.Situacao == 6}
              value="6"
              onChange={(e) => {
                formik.setFieldValue(
                  "Situacao",
                  Number(e.target.value)
                );
                store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
              }}
            />
            Apenas Orçamento
          </label>
        )}

        {props.enviarParaSupervisao && (
          <label id="ResumoOrcamento_LabelParaSupervisao" style={{ cursor: "pointer" }}>
            <input accessKey="o"
              type="radio"
              disabled={props.somenteLeitura || props.processando}
              name="Situacao"
              id="ResumoOrcamento_Situacao_ParaSupervisao"
              checked={formik.values.Situacao == 5}
              value="5"
              onChange={(e) => {
                formik.setFieldValue(
                  "Situacao",
                  Number(e.target.value)
                );
                store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
              }}
            />
            Enviar para Supervisão
          </label>
        )}

        {props.apenasConfirmado && (
          <label id="ResumoOrcamento_LabelApenasConfirmado" style={{ cursor: "pointer" }}>
            <input accessKey="o"
              type="radio"
              disabled={props.somenteLeitura || props.processando}
              name="Situacao"
              id="ResumoOrcamento_Situacao_ApenasConfirmado"
              checked={formik.values.Situacao == 1}
              value="1"
              onChange={(e) => {
                formik.setFieldValue(
                  "Situacao",
                  Number(e.target.value)
                );
                store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
              }}
            />
            Apenas Confirmado
          </label>
        )}

        {props.orcamentoConfirmado && (
          <label id="ResumoOrcamento_LabelOrcamentoConfirmado" style={{ cursor: "pointer" }}>
            <input accessKey="o"
              type="radio"
              name="Situacao"
              id="ResumoOrcamento_Situacao_OrcamentoConfirmado"
              checked={formik.values.Situacao == 1}
              value="1"
              disabled={props.somenteLeitura || props.processando}
              onChange={(e) => {
                formik.setFieldValue(
                  "Situacao",
                  Number(e.target.value)
                );
                store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
              }}
            />
            Orçamento Confirmado
          </label>
        )}
      </div>
    </div>
  </>

  const campoSelecionarFinalidade = <>
    <label id="ResumoOrcamento_LabelFinalidade">Finalidade</label>
    <div id="ResumoOrcamento_FinalidadeContainer">
      <select
        disabled={!formik.values.RequisitarFinalidadeVenda || props.somenteLeitura || props.processando}
        name="FinalidadeVenda"
        id="ResumoOrcamento_Finalidade"
        value={(formik.values?.FinalidadeVenda) ?? ""}
        onChange={(e) => {
          formik.handleChange(e)
          store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
          store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ FinalidadeVenda: e.target.value }))
        }}
      >
        <option value="" id="ResumoOrcamento_Finalidade_Vazia">Selecione</option>
        <option value="R" id="ResumoOrcamento_Finalidade_R">Revenda</option>
        <option value="C"  id="ResumoOrcamento_Finalidade_C">Consumo</option>
      </select>
    </div>
  </>

  const campoInformarDataValidade = <>
    <label 
      id="ResumoOrcamento_LabelDataValidade"
        title="Veja parâmetros:&#013;-Validade obrigatória no orçamento&#013;- Consiste a validade do orçamento?&#013;- Número de dias úteis para validade do orçamento"
    >Data Validade</label>
    <div id="ResumoOrcamento_DataValidadeContainer">
      <InputData
        disabled={props.somenteLeitura || props.processando}
        id="ResumoOrcamento_DataValidadeInput"
        data={globalMU.converterDataHoraFormatoENComTParaDataFormatoPT(formik.values.DataValidade)}
        onChange={e => {
          formik.setFieldValue("DataValidade", e)
          store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
          store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ DataValidade: e }))
        }}
        bloquearDataMenorQueHoje={true}
        selecionaValorOnFocus
      />
    </div>
  </>

  const campoInformarObservacaoNotaFiscal = <>
    <label id="ResumoOrcamento_LabelObservacaoNF">Observação na NF</label>
    <div id="ResumoOrcamento_ObservacaoNFContainer">
      <input
        maxLength={30}
        name="ObservacaoNF"
        id="ResumoOrcamento_ObservacaoNFInput"
        type="text"
        disabled={props.somenteLeitura || props.processando}
        value={(formik.values.ObservacaoNF) ?? ""}
        onChange={(e) => {
          formik.setFieldValue("ObservacaoNF", e.target.value);
          store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
          store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ ObservacaoNF: e.target.value }))
        }}
      />
    </div>
  </>

  const campoInformarObservacaoOrcamento = <>
    <label id="ResumoOrcamento_LabelObservacaoOrcamento">Observação do Orçamento</label>
    <div id="ResumoOrcamento_ObservacaoOrcamentoContainer">
      <textarea
        name="ObservacaoOrcamento"
        id="ResumoOrcamento_ObservacaoOrcamentoInput"
        className={estilosInputs.textareaAutoAjustavel}
        //style={{ width: "100ch", maxWidth: "100%" }}
        maxLength={254}
        disabled={props.somenteLeitura || props.processando}
        cols={60}
        //rows={6}
        value={(formik.values.ObservacaoOrcamento) ?? ""}
        onChange={(e) => {
          formik.setFieldValue("ObservacaoOrcamento", e.target.value);
          store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
          store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ ObservacaoOrcamento: e.target.value }))
        }}
      />
    </div>
  </>

  const campoFontedeReceita = <>{props.tipoOrcamentoDescricao ==="Venda" 
        && valorParametroPorFilial("UTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA") === "S" && (  // 487678
  <> 
    <label id="ResumoOrcamento_LabelFonteReceita">Fonte de Receita</label>
    <div id="ResumoOrcamento_FontedeReceita">{formik.values.NomeFonteReceita}</div>
  </>
  )}
  </>

  const campoSelecionarObservacaoLivre = <>
    <label id="ResumoOrcamento_LabelObservacaoLivre" className="_cursor-pointer" htmlFor="resumoOrcamento-UsarObservacaoTextoLivreNF" >Faz parte do Texto Livre</label>
    <div id="ResumoOrcamento_ObservacaoLivreContainer">
      <input id="resumoOrcamento-UsarObservacaoTextoLivreNF"
        name="UsarObservacaoTextoLivreNF"
        type="checkbox"
        disabled={props.somenteLeitura || props.processando}
        checked={formik.values.UsarObservacaoTextoLivreNF ?? false}
        onChange={(e) => {
          formik.setFieldValue(
            "UsarObservacaoTextoLivreNF",
            e.target.checked
          );
          store.dispatch(gravarTelasAlteradasNaoSalvaAction("telaResumoOrcamento"))
          store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ UsarObservacaoTextoLivreNF: e.target.checked }))
        }}
      />
    </div>
  </>

  const botaoOnAplicar = <div id="ResumoOrcamento_BotaoAplicarContainer">
    {!props.somenteLeitura &&
      <button className={clsx(estilosBotoes.botaoPrimario, "no-global-button")}
        style={{ marginTop: "10px" }}
        disabled={props.processando}
        id="ResumoOrcamento_BotaoAplicar"
        onClick={async () => {
          const erros = await formik.validateForm(formik.values)

          if (Object.keys(erros).length == 0) {
            props.onAplicar(formik.values)
          } else {
            props.onErrorValidacao(erros)
          }
        }}
      >
        Aplicar
      </button>
    }
  </div>

  return (
    <>
      <div id="ResumoOrcamento_ContainerPrincipal">
        <div
          id="ResumoOrcamento_Formulario"
          className={clsx(estilosFormulario.formulario, classeParaLabelsFormularioComFundoEscuro)}>

          {dadosTransferenciaFilialDestino}

          {dadosVendaCliente}

          {dadosFilialVenda}

          {dadosAreaVenda}

          {dadosPlanoPagamentoSemDescricao}

          {dadosPlanoPagamentoComDescricao}

          {dadoDespesasInclusas}

          {dadosDateOrcamento}

          {dadosEntregaPosteriorTurnoEntrega}

          {dadosMonetariosOrcamento}

          {campoSelecionarTipoDeOperacao}

          {campoInformarPedidoDeCompra}

          {campoSelecionarOpcaoOrcamento}

          {campoSelecionarFinalidade}

          {campoInformarDataValidade}

          {campoInformarObservacaoNotaFiscal}

          {campoInformarObservacaoOrcamento}

          {campoSelecionarObservacaoLivre}

          {campoFontedeReceita}
        </div>
      </div>

      {botaoOnAplicar}
      {<section id="ResumoOrcamento_FooterSpacer" style={{ marginBottom: "100px" }} />}
    </>
  );
})